import { AxiosInstance, AxiosPromise } from 'axios';
import md5 from 'md5';
import moment from 'moment';

import { createHttpAxios } from './http';
import { IUser } from '../../modules/security/user/UserModel';

class AuthServiceAPI {
  public httpAxios: AxiosInstance;

  constructor() {
    this.httpAxios = createHttpAxios('user/login', {
      baseURL: process.env.REACT_APP_API_URL
    });
  }

  public login(user: IUser | any): AxiosPromise<any> {
    return this.httpAxios
      .post('', user, { withCredentials: true, maxRedirects: 0 })
      .then(response => response)
      .catch(err => {
        throw err;
      });
  }

  public storeUserCredentials(userdata: any) {
    localStorage.setItem('userdata', JSON.stringify(userdata));
    localStorage.setItem('token', userdata.loginToken);
    const id = userdata?.defaultCommodity?.id;
    if (id && !this.getActiveCommodity()) {
      this.setCommodity(id);
    }
  }

  public setCommodity = (id: number, noUpdateLS?: boolean) => {
    const strCommodity = JSON.stringify({ id });
    sessionStorage.setItem('commodity', strCommodity);
    if (!noUpdateLS) {
      localStorage.setItem('commodity', strCommodity);
    }
  };

  public setIsAutoRefresh = (isAuto: boolean) => {
    const strIsAuto = JSON.stringify({ isAuto });
    sessionStorage.setItem('isAutoRefresh', strIsAuto);
  };

  public getIsAutoRefresh() {
    return sessionStorage?.getItem('isAutoRefresh') === JSON.stringify(true);
  }

  public setCoverSheetStyles = (data: any) => {
    const coverSheetStyles = JSON.stringify({ data });
    sessionStorage.setItem('coverSheetStyles', coverSheetStyles);
  };

  public getCoverSheetStyles = (): any => {
    const coverSheetStyles = sessionStorage.getItem('coverSheetStyles');
    if (coverSheetStyles) {
      return JSON.parse(coverSheetStyles);
    }
    return null;
  };

  public createToken(userdata: any) {
    if (userdata && userdata.alias && userdata.alias.requester && userdata.alias.authenticationPrivateKey) {
      const userToken = userdata.username.toUpperCase() + userdata.alias.authenticationPrivateKey.toUpperCase();
      const userMD5 = md5(`${userToken}${moment().format('YYYY-MM-DD')}${new Date().getUTCHours()}`);
      return btoa(`${userdata.alias.requester}:${userMD5.toUpperCase()}`);
    }
    return '';
  }

  public isLoggedIn() {
    return !!localStorage.getItem('token');
  }

  public getUser(): any {
    const user = localStorage.getItem('userdata');
    if (user) {
      return JSON.parse(user);
    }
    return null;
  }

  public getUserId(): any {
    const user = this.getUser();
    return user?.id ?? -1;
  }

  public getUserName(): any {
    const user = this.getUser();
    return user?.username ?? '';
  }

  public getUserSystemVariables(): any {
    const user = this.getUser();
    if (user && user.hasOwnProperty('systemVariables')) {
      return user.systemVariables;
    }
    return [];
  }

  public clearToken(): void {
    localStorage.removeItem('userdata');
    localStorage.removeItem('token');
    localStorage.removeItem('auth0Token');
    localStorage.removeItem('commodity');
    sessionStorage?.removeItem('commodity');
  }

  public getToken() {
    const token = localStorage.getItem('token');
    if (token) {
      return token;
    }
    return '';
  }

  public getDefaultUserTerminal() {
    const user = this.getUser();
    if (user?.defaultTerminal?.id) {
      return user.defaultTerminal.id;
    }
    return 1;
  }

  public getOrgId() {
    const user = this.getUser();
    return user?.org?.id ?? 0;
  }

  public getEldtabPermission(): boolean {
    const user = this.getUser();
    const { isFromEld = true } = user?.org;
    return isFromEld;
  }

  public getEldType(): string {
    const user = this.getUser();
    const { telematicsELDType = '' } = user?.company;
    return telematicsELDType;
  }

  public getActiveCommodity() {
    const sSCommodity = sessionStorage.getItem('commodity');
    const lSCommodity = localStorage.getItem('commodity');
    const finalCommodity = sSCommodity || lSCommodity;
    if (finalCommodity) {
      const commodity = JSON.parse(finalCommodity);
      if (commodity?.id) {
        return commodity.id;
      }
    }
    return null;
  }

  public getOrgDetailsByName = async (name: any) => {
    try {
      return await this.httpAxios.get(`getOrgDetailsByName/${name}`);
    } catch (error) {
      console.log(error);
      throw (error as Error).message;
    }
  };
}
const authService = new AuthServiceAPI();

export default authService;
